import type {MvsFormDto} from "../../shared/dto/form/dto/mvs-form.dto";
import {UiQlWidgetWorkbenchAccess} from "../interface/ui-ql-widget-workbench-access.interface";

/**
 * Derives "may this user author QL widgets" (ui write on `ui.UiWidget`, requirement.md business
 * rule 4) from the form metadata the backend already sends with a plain template request.
 *
 * THE MECHANISM IS THE EXISTING ONE, not a new endpoint: `FormService.internalCreateForm` calls
 * `form.addCrudActions(objectAccess.getAccess())` for every form it builds, and `Form.addCrudActions`
 * translates the caller's `AuthObjectAccessEnum` into the action set
 * (`full` -> CREATE/DELETE/UPDATE/DISPLAY, `write` -> UPDATE/DISPLAY, `read` -> DISPLAY).
 * The same reading is used by `feature-crm/cr/logic/cr-contact-permission.ts`; it is re-stated here
 * (not imported) because core must not depend on a feature package.
 *
 * WIRE FORMAT: the backend field is a `HashMap<String, FormAction>` (JSON object keyed by action
 * name) while `MvsFormDto.formActions` is declared as `string[]` — both shapes are accepted.
 *
 * THE BOUNDARY IS STILL THE BACKEND: `/ui/qlWidgets/*` answers 403 without ui write whatever this
 * helper says. This check only decides what the page renders up front.
 *
 * Pure and dependency free so it stays unit testable without a TestBed.
 */
export class UiQlWidgetWriteAccess {

    /** `write` access shows up as this action (`full` additionally adds CREATE/DELETE). */
    static readonly ACTION_UPDATE = 'UPDATE';

    /**
     * Read the access out of a `ui.UiWidget` template.
     *
     * DELIBERATE FAIL-OPEN: a form WITHOUT any `formActions` metadata (missing, `null` or not an
     * object/array) answers `granted`. The backend did not say "no", and hiding the whole tool on a
     * backend that simply omits the block would be indistinguishable from a real refusal. This is safe
     * because the check is presentation only — the backend enforces ui write on every
     * `/ui/qlWidgets/*` call (403), and the page demotes to `denied` on the first 401/403 of
     * validate/save ({@link isForbidden}). An EMPTY action set, by contrast, is an explicit "no" →
     * `denied`; a missing form is a broken check → `unknown`.
     *
     * @param form the `ui.UiWidget` template form, `null`/`undefined` if none arrived
     * @returns `granted`, `denied` or `unknown` (see above)
     */
    static fromForm(form: MvsFormDto | null | undefined): UiQlWidgetWorkbenchAccess {

        if (!form) {
            return 'unknown';
        }

        const actions = UiQlWidgetWriteAccess.readActions(form);

        if (actions === null) {
            return 'granted';
        }

        return actions.has(UiQlWidgetWriteAccess.ACTION_UPDATE) ? 'granted' : 'denied';
    }

    /**
     * Classify a failed check. 401/403 are the right answering; anything else (5xx, network) is a
     * broken check — never silently turned into a grant.
     */
    static fromError(error: unknown): UiQlWidgetWorkbenchAccess {
        return UiQlWidgetWriteAccess.isForbidden(error) ? 'denied' : 'unknown';
    }

    /** Only 401/403 count as a refusal; read structurally so an `HttpErrorResponse` and a plain `{status}` both work. */
    static isForbidden(error: unknown): boolean {
        const status = (error && typeof error === 'object') ? (error as { status?: unknown }).status : undefined;
        return status === 403 || status === 401;
    }

    /** @returns null = "the backend said nothing", empty set = "the backend said: no action". */
    protected static readActions(form: MvsFormDto): Set<string> | null {

        // declared as string[] on MvsFormDto, but the wire format is a HashMap<String, FormAction> (see class doc)
        const formActions: unknown = form?.formActions;

        if (formActions == null) {
            return null;
        }

        const actions = new Set<string>();

        if (Array.isArray(formActions)) {
            for (const entry of formActions as unknown[]) {
                const name = typeof entry === 'string' ? entry : UiQlWidgetWriteAccess.readField(entry, 'action');
                UiQlWidgetWriteAccess.addAction(actions, typeof name === 'string' ? name : null, entry);
            }
            return actions;
        }

        if (typeof formActions !== 'object') {
            return null;
        }

        const actionMap = formActions as Record<string, unknown>;
        for (const key of Object.keys(actionMap)) {
            UiQlWidgetWriteAccess.addAction(actions, key, actionMap[key]);
        }

        return actions;
    }

    /** Add one action name, skipping an entry that is explicitly disabled. */
    protected static addAction(actions: Set<string>, name: string | null, entry: unknown): void {

        if (!name || UiQlWidgetWriteAccess.readField(entry, 'enabled') === false) {
            return;
        }

        actions.add(String(name).toUpperCase());
    }

    /** One property of an untyped wire entry; `undefined` for a non-object. */
    private static readField(entry: unknown, field: string): unknown {
        return (entry && typeof entry === 'object') ? (entry as Record<string, unknown>)[field] : undefined;
    }
}
